import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { FileSignature, Shield, Home, Scale, Gavel, Send, Loader2, Download, FileText, MessageSquare, Paperclip, X, Upload, Save, Landmark, Car } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import ContractPortal from './ContractPortal';
import RedressPortal from './RedressPortal';
import ForeclosureDefender from './ForeclosureDefender';
import DocumentVaultPage from './DocumentVaultPage';
import MortgageEqualizer from './MortgageEqualizer';
import AutoLoanRemedy from './AutoLoanRemedy';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const TABS = [
  { key: 'generator', label: 'Remedy Generator', icon: Gavel, color: 'purple' },
  { key: 'mortgage', label: 'Mortgage Equalizer', icon: Landmark, color: 'amber' },
  { key: 'autoloan', label: 'Auto Loan Remedy', icon: Car, color: 'sky' },
  { key: 'foreclosure', label: 'Foreclosure Defender', icon: Home, color: 'emerald' },
  { key: 'contract', label: 'AI Contract Portal', icon: FileSignature, color: 'blue' },
  { key: 'redress', label: 'Redress Portal', icon: Shield, color: 'red' },
];

const SUGGESTED_DOCS = [
  'Cease and Desist Notice',
  'Notice of Conditional Acceptance',
  'Affidavit of Status',
  'Notice of Liability',
  'Demand for Full Accounting',
  'Notice of Rescission',
  'Public Notice / Declaration',
  'Fee Schedule for Rights Violations',
  'Bill in Equity',
];

function RemedyGeneratorTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [subTab, setSubTab] = useState('chat');

  const hasPremiumAccess = user?.subscription_plan === 'premium' || user?.subscription_plan === 'professional';
  const hasLimitedRemedyAccess = user?.subscription_plan === 'pro_se_starter';

  return (
    <div data-testid="remedy-generator-tab">
      {/* Sub-tabs: AI Chat vs Templates */}
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex gap-2">
        <button
          onClick={() => setSubTab('chat')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            subTab === 'chat' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
          }`}
          data-testid="remedy-subtab-chat"
        >
          <MessageSquare className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Document Generator
        </button>
        <button
          onClick={() => setSubTab('vault')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            subTab === 'vault' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
          }`}
          data-testid="remedy-subtab-vault"
        >
          <FileText className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Document Vault
        </button>
      </div>

      {subTab === 'chat' && <RemedyChat />}
      {subTab === 'vault' && <DocumentVaultPage onGoToGenerator={() => setSubTab('chat')} />}
    </div>
  );
}

function RemedyChat() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [lastDocContent, setLastDocContent] = useState(null);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [savedToVault, setSavedToVault] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // { filename, text }
  const [fileUploading, setFileUploading] = useState(false);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const scrollToBottom = () => {
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_BASE}/remedy-generator/extract-text`, formData, { headers });
      if (res.data.success) {
        setPendingFile({ filename: res.data.filename, text: res.data.text });
        toast({ title: 'Document Attached', description: `${res.data.filename} is ready. Add your instructions and send.` });
      }
    } catch (err) {
      toast({ title: 'Upload Failed', description: err.response?.data?.detail || 'Could not process the document.', variant: 'destructive' });
    } finally {
      setFileUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if ((!msg && !pendingFile) || loading) return;
    setInput('');

    // Build the full message with file context
    let fullMessage = msg || `Please analyze the attached document: ${pendingFile?.filename}`;
    const displayMsg = pendingFile ? `[Attached: ${pendingFile.filename}] ${msg || 'Analyze this document'}` : msg;
    
    if (pendingFile) {
      fullMessage = `${msg || 'Please analyze this document and incorporate its contents into the legal document I am requesting.'}\n\n--- ATTACHED DOCUMENT: ${pendingFile.filename} ---\n${pendingFile.text}\n--- END OF ATTACHED DOCUMENT ---`;
    }
    
    const fileRef = pendingFile;
    setPendingFile(null);

    setMessages(prev => [...prev, { role: 'user', content: displayMsg }]);
    setLoading(true);
    scrollToBottom();

    try {
      const res = await axios.post(`${API_BASE}/remedy-generator/chat`, {
        message: fullMessage,
        session_id: sessionId,
      }, { headers });

      if (res.data.success && res.data.job_id) {
        setSessionId(res.data.session_id);
        const jobId = res.data.job_id;
        let attempts = 0;
        const poll = async () => {
          if (attempts >= 60) {
            setLoading(false);
            setMessages(prev => [...prev, { role: 'assistant', content: 'The document is taking longer than expected. Please try again.' }]);
            scrollToBottom();
            return;
          }
          attempts++;
          try {
            const jr = await axios.get(`${API_BASE}/remedy-generator/jobs/${jobId}`, { headers });
            if (jr.data.status === 'completed') {
              setLastDocContent(jr.data.response);
              setMessages(prev => [...prev, { role: 'assistant', content: jr.data.response }]);
              setLoading(false);
              scrollToBottom();
            } else if (jr.data.status === 'failed') {
              setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, an error occurred: ' + (jr.data.error || 'Unknown error') }]);
              setLoading(false);
              scrollToBottom();
            } else {
              setTimeout(poll, 3000);
            }
          } catch {
            setTimeout(poll, 3000);
          }
        };
        poll();
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'AI generation failed.', variant: 'destructive' });
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, an error occurred. Please try again.' }]);
      setLoading(false);
      scrollToBottom();
    }
  };

  const downloadDocx = async () => {
    if (!lastDocContent || downloadingDocx) return;
    setDownloadingDocx(true);
    try {
      const titleMatch = lastDocContent.match(/^([A-Z][A-Z\s,&\-:]+)/m);
      const title = titleMatch ? titleMatch[1].trim().substring(0, 80) : 'Legal Document';

      const res = await axios.post(`${API_BASE}/remedy-generator/generate-docx`, {
        content: lastDocContent,
        title: title,
      }, { headers });

      if (res.data.success) {
        const doc = res.data.document;
        const byteChars = atob(doc.content);
        const byteNums = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
        const blob = new Blob([new Uint8Array(byteNums)], { type: doc.mime_type });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.filename;
        a.click();
        URL.revokeObjectURL(url);
        toast({ title: 'Document Downloaded', description: doc.filename });
      }
    } catch {
      toast({ title: 'Download Failed', variant: 'destructive' });
    } finally {
      setDownloadingDocx(false);
    }
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId('');
    setLastDocContent(null);
    setSavedToVault(false);
  };

  const saveToVault = () => {
    if (!lastDocContent || !user?.email) return;
    const titleMatch = lastDocContent.match(/^([A-Z][A-Z\s,&\-:]+)/m);
    const title = titleMatch ? titleMatch[1].trim().substring(0, 80) : 'AI Generated Document';

    const vaultKey = `document_vault_${user.email}`;
    let existing = [];
    try {
      const saved = localStorage.getItem(vaultKey);
      if (saved) existing = JSON.parse(saved);
    } catch {}

    const newDoc = {
      id: Date.now().toString(),
      templateId: 'ai_generated',
      templateTitle: title,
      formData: { fullName: user.full_name || '' },
      generatedContent: lastDocContent,
      createdAt: new Date().toISOString(),
      userEmail: user.email,
    };

    existing.push(newDoc);
    localStorage.setItem(vaultKey, JSON.stringify(existing));
    setSavedToVault(true);
    toast({ title: 'Saved to Vault', description: `"${title}" has been saved to your Document Vault.` });
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4" data-testid="remedy-generator-chat">
      {/* Header */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Gavel className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
          <div>
            <h2 className="font-semibold text-purple-900">AI Remedy Document Generator</h2>
            <p className="text-sm text-purple-700 mt-1">
              Request any legal document, and our AI Agent will draft it for you. Describe the document you need, provide your facts, and the AI will generate a complete, court-presentable draft you can download as a Word document.
            </p>
          </div>
        </div>
      </div>

      {/* Quick suggestions — show only when no messages */}
      {messages.length === 0 && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 font-medium">Quick start — click a document type, then add your facts before sending:</p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTED_DOCS.map(doc => (
              <button
                key={doc}
                onClick={() => {
                  if (doc === 'Bill in Equity') {
                    setInput(`Draft a Bill in Equity for me. Here are my facts:\n\nFull Name: \nAddress: \nCourt Name / Courtroom: \nCase Number: \nOpposing Party: \nNature of Equitable Claim: \nFacts of the Case: `);
                  } else {
                    setInput(`Draft a ${doc} for me. Here are my facts:\n\nFull Name: \nAddress: \nOpposing Party: \nDetails: `);
                  }
                }}
                className="text-xs px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full border border-purple-200 hover:bg-purple-100 transition-colors"
                data-testid={`suggest-${doc.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {doc}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chat messages */}
      <Card className="flex flex-col" style={{ minHeight: '450px' }}>
        <CardContent className="flex-1 flex flex-col pt-4">
          <div className="flex-1 overflow-y-auto space-y-4 mb-4 max-h-[500px]">
            {messages.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <Gavel className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">AI Remedy Document Generator</p>
                <p className="text-sm mt-1">Tell the AI what legal document you need. Be specific about the parties, facts, and type of document.</p>
                <p className="text-xs mt-3 text-gray-400">All documents grounded in Equity Jurisprudence, English Common Law & the Magna Carta</p>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`rounded-lg px-4 py-2 max-w-[85%] ${
                    msg.role === 'user'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                    <span className="text-sm text-gray-600">AI is drafting your document...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Download bar */}
          {lastDocContent && !loading && (
            <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg mb-3">
              <FileText className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="text-sm text-green-800 flex-1">Document ready</span>
              <Button
                size="sm"
                onClick={downloadDocx}
                disabled={downloadingDocx}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="remedy-download-docx"
              >
                {downloadingDocx ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                Download DOCX
              </Button>
              <Button
                size="sm"
                onClick={saveToVault}
                disabled={savedToVault}
                variant={savedToVault ? "ghost" : "outline"}
                className={savedToVault ? "text-green-700 cursor-default" : "border-green-300 text-green-700 hover:bg-green-100"}
                data-testid="remedy-save-vault"
              >
                <Save className="w-3.5 h-3.5 mr-1.5" />
                {savedToVault ? 'Saved to Vault' : 'Save to Vault'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={startNewSession}
                className="border-green-300 text-green-700 hover:bg-green-100"
                data-testid="remedy-new-session"
              >
                New Document
              </Button>
            </div>
          )}

          {/* Pending file indicator */}
          {pendingFile && (
            <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg mb-2">
              <Paperclip className="w-4 h-4 text-purple-600 flex-shrink-0" />
              <span className="text-sm text-purple-800 flex-1 truncate">{pendingFile.filename}</span>
              <button onClick={() => setPendingFile(null)} className="text-purple-400 hover:text-purple-600" data-testid="remedy-remove-file">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Input */}
          <div className="flex gap-2 items-end">
            {/* File upload button */}
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
              onChange={handleFileUpload}
              data-testid="remedy-file-input"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || fileUploading}
              className="flex-shrink-0 h-10 w-10 border-gray-300"
              title="Attach a document"
              data-testid="remedy-attach-btn"
            >
              {fileUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
            </Button>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder={pendingFile ? "Add instructions for the attached document..." : "Describe the legal document you need and provide your facts..."}
              disabled={loading}
              rows={input.includes('\n') ? Math.min(input.split('\n').length + 1, 6) : 2}
              className="flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
              data-testid="remedy-chat-input"
            />
            <Button
              onClick={() => sendMessage()}
              disabled={loading || (!input.trim() && !pendingFile)}
              className="bg-purple-600 hover:bg-purple-700 h-10"
              data-testid="remedy-chat-send"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function RemedyAgents() {
  const [activeTab, setActiveTab] = useState('generator');

  const activeClasses = {
    purple: 'border-purple-600 text-purple-700',
    amber: 'border-amber-600 text-amber-700',
    sky: 'border-sky-600 text-sky-700',
    emerald: 'border-emerald-600 text-emerald-700',
    blue: 'border-blue-600 text-blue-700',
    red: 'border-red-600 text-red-700',
  };

  return (
    <div className="flex flex-col h-full" data-testid="remedy-agents-page">
      {/* Tab bar */}
      <div className="bg-white border-b border-gray-200 px-4 pt-4 pb-0 sticky top-0 z-10">
        <div className="flex items-center gap-2 mb-3">
          <Scale className="w-6 h-6 text-indigo-600" />
          <h1 className="text-xl font-bold text-gray-900">Remedy AI Agents</h1>
        </div>
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive ? activeClasses[tab.color] : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid={`remedy-tab-${tab.key}`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ').pop()}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'generator' && <RemedyGeneratorTab />}
        {activeTab === 'mortgage' && <MortgageEqualizer />}
        {activeTab === 'autoloan' && <AutoLoanRemedy />}
        {activeTab === 'foreclosure' && <ForeclosureDefender />}
        {activeTab === 'contract' && <ContractPortal />}
        {activeTab === 'redress' && <RedressPortal />}
      </div>
    </div>
  );
}
